import {
  calendarTileState,
  mailboxTileState,
  phoneTileState,
} from './integrationState';
import type { TileState } from './integrationState';
import type {
  CalendarConnectionsResponse,
  MailboxChannelResponse,
  PhoneStatusResponse,
} from '@biasafe.models';

/**
 * What the "Connect your tools" step says about each row.
 *
 * THE ROWS READ THE SAME TILE STATES THE INTEGRATIONS PAGE DRAWS
 * (`integrationState.ts`), not a second reading of the status endpoints. A
 * second reading is how "Connected" on one screen and "Reconnect" on the other
 * would come to describe one grant. This file only maps those states onto the
 * five things the step needs to say, plus the two the step adds on top: a row
 * held back by two-factor setup, and a row only an administrator can finish.
 *
 * `unconfigured` ALWAYS WINS ITS OWN ROW and carries no button: nothing the user
 * can do fixes a credential this deployment does not hold (the `*_configured`
 * flags on the status endpoints). Staging and production therefore differ only
 * by what each one has configured, never by a flag in this code.
 */
export type ToolStatus =
  | 'loading'
  | 'connected'
  /** This deployment holds no credential for it. No button. */
  | 'unconfigured'
  /** Not connected, and the user can connect it now. */
  | 'todo'
  /** Connected before and failing, or the state could not be read, or blocked. */
  | 'attention'
  /** Only an administrator can connect it; the user can send them the link. */
  | 'ask_admin';

export interface ToolView {
  status: ToolStatus;
  /** One sentence for the row, or null when the status says it all. */
  detail: string | null;
  /** What the row's button does, or null when it has none. */
  action: 'connect' | 'reconnect' | 'retry' | null;
}

type QueryLike<T> = { isLoading?: boolean; isError?: boolean; data?: T };

const COULD_NOT_READ = 'Could not check this connection. Try again.';

function fromTile(tile: TileState): ToolView {
  switch (tile.kind) {
    case 'loading':
      return { status: 'loading', detail: null, action: null };
    case 'error':
      return { status: 'attention', detail: COULD_NOT_READ, action: 'retry' };
    case 'unconfigured':
      return {
        status: 'unconfigured',
        detail: 'Not set up on this environment.',
        action: null,
      };
    case 'connect':
      return { status: 'todo', detail: null, action: 'connect' };
    case 'switch':
      return {
        status: 'attention',
        detail: `${tile.other} is already connected as your mailbox. Disconnect it in Integrations first, because only one mailbox can be connected at a time.`,
        action: null,
      };
    case 'connected':
      return { status: 'connected', detail: null, action: null };
    case 'reconnect':
      return { status: 'attention', detail: tile.message, action: 'reconnect' };
  }
}

export function mailboxToolView(
  query: QueryLike<MailboxChannelResponse>,
  provider: 'GOOGLE' | 'MICROSOFT',
): ToolView {
  return fromTile(mailboxTileState(query, provider));
}

export function calendarToolView(
  query: QueryLike<CalendarConnectionsResponse>,
  provider: 'GOOGLE' | 'OUTLOOK',
): ToolView {
  return fromTile(calendarTileState(query, provider));
}

/**
 * Teams Phone. THE FIRM'S CONNECTION, and one that only a Microsoft 365 tenant
 * administrator can approve.
 *
 * WHAT THE BROWSER CAN AND CANNOT KNOW. Whether the signed-in person is a tenant
 * administrator is proven only by the `wids` claim of a Microsoft id_token that
 * the SERVER verifies during the connect (`phone/identity.py`); the Identity
 * Platform session carries no such claim, so nothing in the browser can say it
 * beforehand. What it can know is `canManage`, the server's "this person is an
 * organization admin", which is the same lock the Integrations card uses.
 *
 *   * not `canManage`: no button, and the row says to ask an administrator.
 *   * `canManage`: the button is drawn (never pressed for them), and the row
 *     says up front that a Microsoft 365 global administrator has to approve.
 *     If that person is not one, the server refuses the sign-in and the step
 *     moves the row to `ask_admin` (see `askAdminAfterFailure` in the step).
 */
export function phoneToolView(
  query: QueryLike<PhoneStatusResponse>,
  isOrgAdmin: boolean,
): ToolView {
  const tile = phoneTileState(query, isOrgAdmin);
  switch (tile.kind) {
    case 'loading':
      return { status: 'loading', detail: null, action: null };
    case 'error':
      return { status: 'attention', detail: COULD_NOT_READ, action: 'retry' };
    case 'unconfigured':
      return {
        status: 'unconfigured',
        detail: 'Not set up on this environment.',
        action: null,
      };
    case 'connected':
      return { status: 'connected', detail: null, action: null };
    case 'not_connected':
      return tile.canManage
        ? {
            status: 'todo',
            detail:
              'A Microsoft 365 global administrator approves access for your whole firm. Only call details are read, never audio.',
            action: 'connect',
          }
        : { status: 'ask_admin', detail: null, action: null };
    case 'attention':
      return tile.canManage
        ? { status: 'attention', detail: tile.message, action: 'reconnect' }
        : { status: 'ask_admin', detail: tile.message, action: null };
  }
}

/** A row counts as dealt with when there is nothing left for the user to do on it. */
export function isSettled(status: ToolStatus, skipped: boolean): boolean {
  return (
    skipped ||
    status === 'connected' ||
    status === 'unconfigured' ||
    status === 'ask_admin'
  );
}

/** Which Google or Microsoft account the user signed in with, when it says. */
export type SignupProvider = 'GOOGLE' | 'MICROSOFT';

/**
 * The provider behind a sign-in, from Identity Platform's provider ids.
 *
 * `null` is "cannot tell": an email and password account, or one linked to
 * both. The step then asks, rather than guessing which mailbox to offer.
 */
export function providerFromIds(
  ids: ReadonlyArray<string>,
): SignupProvider | null {
  const google = ids.includes('google.com');
  const microsoft = ids.includes('microsoft.com');
  if (google === microsoft) return null;
  return google ? 'GOOGLE' : 'MICROSOFT';
}

/**
 * What an advisor sends to the person who can approve Teams Phone. Plain text so
 * it pastes into any chat or email, with the link to the page where it is done.
 */
export function adminRequestText(origin: string): string {
  return [
    'Please connect Microsoft Teams Phone for our firm in BIASafe.',
    `Open ${origin}/integrations, choose Microsoft Teams Phone and press Connect. A Microsoft 365 global administrator has to approve it.`,
    'It reads call details only (who, when and for how long). Audio is never recorded or stored.',
  ].join('\n');
}
